import { TextAreaField, type TextAreaFieldProps } from '@vizit/molecules';
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form';

interface FormTextAreaProps<T extends FieldValues>
  extends Omit<TextAreaFieldProps, 'errorMessage' | 'isInvalid' | 'onChange' | 'value'> {
  /**
   * The control object from the `useForm` hook.
   */
  control: Control<T>;
  /**
   * The name of the field.
   */
  name: Path<T>;
}

/**
 * A TextAreaField wired to react-hook-form: value, error and disabled state
 * come from the form, so callers only pass `control` and `name`. A caller's
 * `onBlur` runs before the form's own blur handling.
 */
function FormTextArea<T extends FieldValues>(props: FormTextAreaProps<T>) {
  const { name, control, isDisabled, onBlur, ...other } = props;

  const { field, fieldState, formState } = useController({ name, control });

  const handleBlur: TextAreaFieldProps['onBlur'] = (event) => {
    onBlur?.(event);
    field.onBlur();
  };

  return (
    <TextAreaField
      {...other}
      errorMessage={fieldState.error?.message}
      textAreaRef={field.ref}
      isDisabled={formState.isSubmitting || isDisabled}
      isInvalid={fieldState.invalid}
      name={name}
      value={field.value ?? ''}
      onChange={field.onChange}
      onBlur={handleBlur}
    />
  );
}

export { FormTextArea };
export type { FormTextAreaProps };
